import { Link, NavLink, useLocation } from "react-router-dom";
import { Home, Calendar, Calculator, LayoutGrid, User, Phone, MessageSquare, LogOut, Menu, X } from "lucide-react";
import { useState } from "react";
import { BRAND } from "../lib/config";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import PWAInstallBanner, { InstallAppButton } from "./PWAInstallBanner";
import LiveChat from "./LiveChat";
import Logo from "./Logo";

const TopNav = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/", label: "Home" },
    { to: "/book", label: "Book Service" },
    { to: "/estimate", label: "Get Estimate" },
    { to: "/gallery", label: "Gallery" },
    { to: "/reviews", label: "Reviews" },
  ];

  return (
    <header className="sticky top-0 z-40 w-full backdrop-blur-xl bg-white/85 border-b border-gray-200" data-testid="top-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-logo">
          <Logo size={40} />
          <div className="leading-tight">
            <div className="font-extrabold text-[15px] tracking-tight text-gray-900 font-[Outfit]">JDSN</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#047857] font-semibold">Lawn Care</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-tight transition-colors ${isActive ? "text-[#047857]" : "text-gray-700 hover:text-gray-900"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <a href={`tel:${BRAND.phoneTel}`} data-testid="top-call-btn" className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-[#047857] px-3 py-2">
            <Phone className="w-4 h-4" /> {BRAND.phone}
          </a>
          {user ? (
            <>
              <Link to="/profile" data-testid="nav-profile">
                <Button variant="ghost" className="rounded-full">Profile</Button>
              </Link>
              <Button onClick={logout} variant="outline" className="rounded-full" data-testid="logout-btn"><LogOut className="w-4 h-4 mr-1" /> Logout</Button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login">
              <Button variant="outline" className="rounded-full">Sign In</Button>
            </Link>
          )}
          <Link to="/book" data-testid="top-book-btn">
            <Button className="rounded-full bg-gradient-to-br from-[#10B981] to-[#047857] hover:opacity-90 text-white font-semibold px-5 shadow-md shadow-emerald-600/20">Book Now</Button>
          </Link>
        </div>

        <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" className="md:hidden p-2 text-gray-900" aria-label="Open menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)}
                className={({ isActive }) => `px-3 py-3 rounded-xl text-base font-medium ${isActive ? "bg-[#047857]/10 text-[#047857]" : "text-gray-800 hover:bg-gray-50"}`}>
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <Link to="/profile" onClick={() => setOpen(false)} className="px-3 py-3 rounded-xl text-base font-medium text-gray-800 hover:bg-gray-50">My Profile</Link>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-3 rounded-xl text-base font-medium text-gray-800 hover:bg-gray-50">Sign In</Link>
            )}
            <a href={`tel:${BRAND.phoneTel}`} className="px-3 py-3 rounded-xl text-base font-medium text-[#047857]">
              <Phone className="w-4 h-4 inline mr-2" /> {BRAND.phone}
            </a>
            <div className="px-3 pt-1">
              <InstallAppButton className="w-full justify-center" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

const BottomNav = () => {
  const location = useLocation();
  // Hide bottom nav on full-bleed flows where we have our own sticky CTA (e.g. checkout)
  if (location.pathname.startsWith("/checkout/") || location.pathname.startsWith("/payment/")) return null;
  const isActive = (p) => (p === "/" ? location.pathname === "/" : location.pathname.startsWith(p));
  const items = [
    { to: "/", label: "Home", icon: Home, testid: "bottom-nav-home" },
    { to: "/book", label: "Book", icon: Calendar, testid: "bottom-nav-book" },
    { to: "/estimate", label: "Estimate", icon: Calculator, testid: "bottom-nav-estimate" },
    { to: "/dashboard", label: "Dashboard", icon: LayoutGrid, testid: "bottom-nav-dashboard" },
    { to: "/profile", label: "Profile", icon: User, testid: "bottom-nav-profile" },
  ];
  return (
    <nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-2xl shadow-black/5"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      data-testid="bottom-nav"
    >
      <div className="grid grid-cols-5">
        {items.map(({ to, label, icon: Icon, testid }) => {
          const active = isActive(to);
          return (
            <Link key={to} to={to} data-testid={testid}
              className={`flex flex-col items-center justify-center py-2.5 gap-0.5 transition-all ${active ? "text-[#10B981]" : "text-gray-400"}`}>
              <Icon className={`w-5 h-5 ${active ? "stroke-[2.5]" : "stroke-2"}`} />
              <span className={`text-[10px] ${active ? "font-bold" : "font-medium"}`}>{label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

// Sticky Call (mobile, lowest of the three floating buttons)
const StickyCall = () => (
  <a href={`tel:${BRAND.phoneTel}`} data-testid="sticky-call-btn"
    className="md:hidden fixed right-4 z-30 w-14 h-14 rounded-full bg-gradient-to-br from-[#10B981] to-[#047857] text-white shadow-2xl shadow-emerald-600/30 flex items-center justify-center active:scale-95 transition-transform"
    style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 160px)" }}
    aria-label="Call now">
    <Phone className="w-6 h-6" />
  </a>
);

// Sticky Text Us (mobile) — opens native SMS to BRAND.phone
const StickyText = () => (
  <a href={`sms:${BRAND.phoneTel}`} data-testid="sticky-text-btn"
    className="md:hidden fixed right-4 z-30 w-14 h-14 rounded-full bg-[#0A2E1E] text-white shadow-2xl shadow-black/30 flex items-center justify-center active:scale-95 transition-transform border border-emerald-500/30"
    style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 224px)" }}
    aria-label="Text us">
    <MessageSquare className="w-6 h-6" />
  </a>
);

const Footer = () => (
  <footer className="border-t border-gray-200 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
      <div>
        <div className="flex items-center gap-2.5 mb-3">
          <Logo size={44} />
          <div className="font-extrabold tracking-tight text-gray-900 font-[Outfit]">JDSN Lawn Care</div>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed">Premium lawn & landscape services in Jacksonville, Florida. Crisp lines. Honest prices.</p>
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#047857] font-bold mb-3">Company</div>
        <ul className="space-y-2 text-sm text-gray-700">
          <li><Link to="/" className="hover:text-gray-900">Home</Link></li>
          <li><Link to="/book" className="hover:text-gray-900">Book Service</Link></li>
          <li><Link to="/estimate" className="hover:text-gray-900">Free Estimate</Link></li>
          <li><Link to="/gallery" className="hover:text-gray-900">Before & After</Link></li>
        </ul>
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#047857] font-bold mb-3">Contact</div>
        <ul className="space-y-2 text-sm text-gray-700">
          <li><a href={`tel:${BRAND.phoneTel}`} className="hover:text-gray-900">{BRAND.phone}</a></li>
          <li><a href={`sms:${BRAND.phoneTel}`} className="hover:text-gray-900">Text us</a></li>
          <li>Jacksonville, Florida</li>
          <li>Mon–Sat · 7am–7pm</li>
        </ul>
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#047857] font-bold mb-3">Pay</div>
        <ul className="space-y-2 text-sm text-gray-700">
          <li>Credit / Debit (Stripe)</li>
          <li>Apple Pay · Google Pay</li>
          <li>Visa · Mastercard · Amex</li>
          <li>Save card · One-tap rebook</li>
        </ul>
      </div>
    </div>
    <div className="border-t border-gray-200 py-5 text-center text-xs text-gray-500">© {new Date().getFullYear()} JDSN Lawn Care · Jacksonville, FL · Licensed & Insured</div>
  </footer>
);

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[#F9FAFB]">
      <TopNav />
      <main className="flex-1 pb-24 md:pb-0">{children}</main>
      <Footer />
      <StickyText />
      <StickyCall />
      <LiveChat />
      <PWAInstallBanner />
      <BottomNav />
    </div>
  );
}
